'use client';

import Link from 'next/link';
import { useMicroSource, type MicroContentItem as MicroItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { MicroItemCard } from './_MicroItemCard';
import { useMicroCardDecorations } from './_useMicroCardDecorations';
import { useMicroPopup } from './popup/MicroPopupHost';

/**
 * One hand-picked micro item — a single quiz / short video / weekly read
 * dropped anywhere on the site, not a feed.
 *
 * <p>The rails and grids in this folder all choose their items from a
 * feed ({@code featured} / {@code latest} / {@code favorites} / …). This
 * one is the curated counterpart: the operator names ONE item by id and
 * it renders wherever the block sits — a quiz on the platform home, a
 * weekly read in a marketing page's sidebar.
 *
 * <p>Click behaviour is the family's: with a {@code MicroPopupHost}
 * mounted the card plays in the popup (micro has no detail route), and
 * the href template is only the fallback for hosts without one.
 *
 * <p>Note the name overlap: the runtime's `MicroContentItem` is the DATA
 * shape of one micro item (aliased to `MicroItem` below), while this is the
 * block that renders one. Same name, two layers.
 *
 * <p>Silent by design when there is nothing to show — no id authored, the
 * id no longer resolves (item unpublished or deleted), or the fetch fails.
 * A public page must not carry a broken card or an authoring note; the
 * designer's own canvas preview is where an operator is told to pick an
 * item.
 */
export interface MicroContentItemProps {
  /** Id of the micro item to feature. Nothing renders without it. */
  contentId?: string | number;
  /** Section heading above the card. Blank renders no heading. */
  heading?: string;
  /** Optional call-to-action under the card. Blank renders no CTA — the
   *  card itself is already the click target. */
  ctaLabel?: string;
  /** Fallback link target when no popup host is mounted. Placeholders:
   *  `{kindSlug}`, `{id}`. */
  itemHrefTemplate?: string;
  /** Show the content-kind chip over the cover. */
  showKindBadge?: boolean;
}

const DEFAULT_HREF_TEMPLATE = '/lxp/micro-learning/{kindSlug}';

export function MicroContentItem({
  contentId,
  heading,
  ctaLabel,
  itemHrefTemplate = DEFAULT_HREF_TEMPLATE,
  showKindBadge = true,
}: MicroContentItemProps) {
  const micro = useMicroSource();
  const popup = useMicroPopup();
  const decorations = useMicroCardDecorations();

  const state = useAsyncResource<MicroItem>(
    async (signal) => {
      // No id authored = nothing to ask the backend for. Checked before
      // the adapter so an unconfigured block never costs a round trip.
      if (contentId == null || contentId === '') return null;
      if (!micro?.content) return null;
      return await micro.content.fetchById(contentId, signal);
    },
    [micro, contentId],
  );

  if (contentId == null || contentId === '') return null;

  if (!micro?.content) {
    return (
      <SectionShell title={heading}>
        <AdapterPlaceholder blockType="MicroContentItem" adapterName="MicroSource.content" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={heading}>
        <div style={{ maxWidth: 420 }}>
          <SkeletonRow count={1} layout="grid" />
        </div>
      </SectionShell>
    );
  }

  // Unresolvable id, or the fetch failed — see the "silent by design" note.
  if (state.kind !== 'loaded') return null;

  const item = state.data;
  const href = itemHrefTemplate
    .replace('{kindSlug}', encodeURIComponent(item.kindSlug))
    .replace('{id}', encodeURIComponent(String(item.id)));
  const cta = ctaLabel?.trim();

  return (
    <SectionShell title={heading}>
      <div style={{ maxWidth: 420, display: 'grid', gap: 'var(--space-3, 12px)' }}>
        <MicroItemCard
          item={item}
          showKindBadge={showKindBadge}
          progress={decorations.progressFor(item.id)}
          categoryName={decorations.categoryNameFor(item.categoryId)}
          hrefTemplate={itemHrefTemplate}
        />
        {cta ? (
          // Mirrors the card's own routing so the two never disagree: play
          // in the popup where a host is mounted, navigate otherwise.
          popup ? (
            <button type="button" onClick={() => popup.open(item)} style={ctaStyle}>
              {cta}
            </button>
          ) : (
            <Link href={href} style={ctaStyle}>
              {cta}
            </Link>
          )
        ) : null}
      </div>
    </SectionShell>
  );
}

const ctaStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-brand-200, #c2d3f7)',
  background: 'var(--color-brand-50, #eff6ff)',
  color: 'var(--color-brand-700, #1d4ed8)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  fontFamily: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
} as const;
